import { chipClasses, type ChipProps } from '@mui/material/Chip';
import { alpha, type Components, lighten, type Theme } from '@mui/material/styles';

import getStyleQuery from '../../../utils/tools/getStyleQuery';
import { getAccessibleTextColor } from '../../getAccessibleTextColor';

// ----------------------------------------------------------------------

const COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const;

// NEW VARIANT
declare module '@mui/material/Chip' {
    interface ChipPropsVariantOverrides {
        soft: true;
    }
}

// ----------------------------------------------------------------------

export function chip(theme: Theme): Components<Theme> {
    const lightMode = theme.palette.mode === 'light';

    const rootStyles = (ownerState: ChipProps) => {
        const defaultColor = ownerState.color === 'default';

        const filledVariant = ownerState.variant === 'filled';

        const outlinedVariant = ownerState.variant === 'outlined';

        const softVariant = ownerState.variant === 'soft';

        const defaultStyle = {
            [`& .${chipClasses.deleteIcon}`]: {
                opacity: 0.48,
                color: 'currentColor',
                '&:hover': {
                    opacity: 1,
                    color: 'currentColor',
                },
            },

            ...(defaultColor && {
                [`& .${chipClasses.avatar}`]: {
                    color: theme.palette.text.primary,
                },
                // FILLED
                ...(filledVariant && {
                    color: lightMode ? theme.palette.common.white : theme.palette.grey[800],
                    backgroundColor: theme.palette.text.primary,
                    '&:hover': {
                        backgroundColor: lightMode ? theme.palette.grey[700] : theme.palette.grey[100],
                    },
                    [`& .${chipClasses.icon}`]: {
                        color: lightMode ? theme.palette.common.white : theme.palette.grey[800],
                    },
                }),
                // OUTLINED
                ...(outlinedVariant && {
                    border: `solid 1px ${alpha(theme.palette.grey[500], 0.32)}`,
                }),
                // SOFT
                ...(softVariant && {
                    color: theme.palette.text.primary,
                    backgroundColor: alpha(theme.palette.grey[500], 0.16),
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.grey[500], 0.32),
                    },
                }),
            }),
        };

        const colorStyle = COLORS.map((color) => {
            const baseBgColor = lighten(theme.palette[color].main, 0.85);
            const baseTextColor = theme.palette[color][lightMode ? 'dark' : 'light'];

            // Adjust text color if contrast is too low
            const accessibleTextColor = getAccessibleTextColor(theme, baseBgColor, baseTextColor);

            return {
                ...(ownerState.color === color && {
                    [`& .${chipClasses.avatar}`]: {
                        color: theme.palette[color].lighter,
                        backgroundColor: theme.palette[color].dark,
                    },
                    // SOFT
                    ...(softVariant && {
                        color: accessibleTextColor,
                        backgroundColor: baseBgColor,
                        '&:hover': {
                            backgroundColor: alpha(theme.palette[color].main, 0.32),
                        },
                    }),
                }),
            };
        });

        const disabledState = {
            [`&.${chipClasses.disabled}`]: {
                opacity: 1,
                color: theme.palette.action.disabled,
                [`& .${chipClasses.icon}`]: {
                    color: theme.palette.action.disabled,
                },
                [`& .${chipClasses.avatar}`]: {
                    color: theme.palette.action.disabled,
                    backgroundColor: theme.palette.action.disabledBackground,
                },
                // FILLED
                ...(filledVariant && {
                    backgroundColor: theme.palette.action.disabledBackground,
                }),
                // OUTLINED
                ...(outlinedVariant && {
                    borderColor: theme.palette.action.disabledBackground,
                }),
                // SOFT
                ...(softVariant && {
                    backgroundColor: theme.palette.action.disabledBackground,
                }),
            },
        };

        return [
            defaultStyle,
            ...colorStyle,
            disabledState,
            {
                fontSize: theme?.rawTypography?.body1?.fontSize,
                fontWeight: 500,
                borderRadius: Number(theme.shape.borderRadius),
            },
        ];
    };

    return {
        MuiChip: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: ChipProps }) => ({
                    [getStyleQuery()]: rootStyles(ownerState),
                }),
            },
        },
    };
}
